/** 首屏、路由、页签重建和前台请求共用 headScript 中的唯一加载控制器。 */
export interface PageLoadingOptions {
  immediate?: boolean;
  timeoutMs?: number;
}

/** 首屏动画的相位起点与周期 —— AppSplash 据此把动画接到 boot 那一份的相位上 */
export interface SplashPhase {
  /** 占位动画开始的时间戳（epoch ms）；0 表示这次首屏没播过占位动画 */
  epoch: number;
  /** 动画周期（ms），与 styles/appSplash.css 的 5.5s 一致 */
  cycleMs: number;
}

interface PageLoadingController {
  start: (label?: string, options?: PageLoadingOptions) => () => void;
  finishBoot: () => void;
  hasPending: () => boolean;
  recentlyInteractive: () => boolean;
  /** 旧版 headScript 里没有这个字段，读取时必须用可选链 */
  splashPhase?: () => SplashPhase;
}

declare global {
  interface Window {
    __dingyanPageLoading?: PageLoadingController;
  }
}

const noop = () => {};
const getController = () => typeof window === 'undefined' ? undefined : window.__dingyanPageLoading;

export function startPageLoading(label = '正在加载页面', options?: PageLoadingOptions): () => void {
  return getController()?.start(label, options) || noop;
}

/** 等 React 提交与页面绘制后交接；取消时也释放该任务，避免卸载遗留。 */
export function finishPageLoadingAfterPaint(end: () => void): () => void {
  if (typeof window === 'undefined') {
    end();
    return noop;
  }
  let firstFrame = 0;
  let secondFrame = 0;
  let fallback = 0;
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    window.cancelAnimationFrame(firstFrame);
    window.cancelAnimationFrame(secondFrame);
    window.clearTimeout(fallback);
    end();
  };
  // 隐藏窗口会暂停 RAF，仍需释放已完成的任务。
  fallback = window.setTimeout(settle, 200);
  firstFrame = window.requestAnimationFrame(() => {
    secondFrame = window.requestAnimationFrame(settle);
  });
  return settle;
}

export function finishBootPageLoading(): () => void {
  return finishPageLoadingAfterPaint(() => getController()?.finishBoot());
}

let finishNavigation = noop;
const requestTasks = new Set<() => void>();
export function beginRoutePageLoading(): void {
  // 重定向或快速连点只替换路由任务；每个请求/fallback仍释放自己的任务。
  finishNavigation();
  // 上一页仍在后台完成的请求不拖住新页，也不会结束新页持有的任务。
  requestTasks.forEach((end) => { end(); });
  const end = startPageLoading('正在切换页面', { immediate: true });
  finishNavigation = finishPageLoadingAfterPaint(end);
}

type LoadingRequestOptions = {
  url?: string;
  pageLoading?: boolean;
  background?: boolean;
  skipGlobalLoading?: boolean;
  signal?: Pick<AbortSignal, 'aborted' | 'addEventListener' | 'removeEventListener'>;
  cancelToken?: {
    reason?: unknown;
    subscribe?: (listener: () => void) => void;
    unsubscribe?: (listener: () => void) => void;
  };
  __dingyanFinishPageLoading?: () => void;
};

export function startRequestPageLoading(config: LoadingRequestOptions): void {
  const controller = getController();
  if (!controller || config.__dingyanFinishPageLoading || config.pageLoading === false ||
    config.background || config.skipGlobalLoading || config.signal?.aborted || config.cancelToken?.reason ||
    document.visibilityState === 'hidden') return;
  // 空闲后台轮询不主动亮条；首屏/切页/刷新及点击、键盘触发的请求参与反馈。
  if (config.pageLoading !== true && !controller.hasPending() && !controller.recentlyInteractive()) return;
  const timeoutMs = 120000;
  const finish = controller.start('正在加载数据', { timeoutMs });
  let closed = false;
  let deadline = 0;
  const end = () => {
    if (closed) return;
    closed = true;
    window.clearTimeout(deadline);
    config.signal?.removeEventListener('abort', end);
    config.cancelToken?.unsubscribe?.(end);
    requestTasks.delete(end);
    finish();
  };
  requestTasks.add(end);
  config.__dingyanFinishPageLoading = end;
  // 与视觉任务同期限清理簿记和取消订阅，不改变原请求的超时/取消行为。
  deadline = window.setTimeout(end, timeoutMs);
  // Axios 0.27 的取消错误不一定带config，直接订阅取消信号才可可靠收尾。
  config.signal?.addEventListener('abort', end, { once: true });
  config.cancelToken?.subscribe?.(end);
}

export function finishRequestPageLoading(config?: LoadingRequestOptions): void {
  const end = config?.__dingyanFinishPageLoading;
  if (!end) return;
  delete config.__dingyanFinishPageLoading;
  finishPageLoadingAfterPaint(end);
}
